/**
 * Version freshness (perf/stability round, task #125; auto-update 2026-08-02).
 *
 * The Hub's index.html is no-store, but iOS PWAs and long-lived phone tabs
 * routinely come back from the background days later still running an old
 * bundle, and the only signal used to be a chunk-load failure. This hook
 * checks /version.json (written by scripts/build.mjs with the SAME value
 * main.tsx sees as VITE_APP_VERSION, served no-store) on mount, whenever the
 * tab becomes visible again, on route changes, and when it is restored from
 * the back/forward cache (pageshow with persisted=true).
 *
 * WHAT CHANGED, and why the original rule is still honoured.
 *
 * This used to never reload on its own - it only reported "stale" and let
 * <FreshnessPill /> offer a manual Reload, because the person may be
 * mid-form. That instinct was correct and an unconditional auto-reload would
 * still be wrong: it destroys in-progress work with no warning. But an
 * update that only lands when somebody notices a pill is an update that does
 * not land. Bobbie closed and reopened the installed PWA several times
 * against a fresh deploy on 2026-08-01 and never got it, partly because of
 * this file's old 5-minute hidden gate (below) and partly because index.html
 * was must-revalidate rather than no-store.
 *
 * So it is now CONDITIONAL: reload if clean, prompt if dirty, and nothing
 * ever overrides dirty (see decideAutoUpdate in unsavedWork.ts for why the
 * "nightly floor" that once did was removed). Everything risky is behind
 * that one pure, tested function.
 *
 * THE 5-MINUTE GATE IS GONE. The old code only checked when the tab had been
 * hidden for more than five minutes, which made the bug self-reinforcing:
 * closing and reopening quickly - exactly what somebody does when the app
 * looks wrong - guaranteed no check at all. It was also redundant, because
 * the sessionStorage guard below already caps checks at one a minute. A
 * short grace period remains only so the iOS keyboard and the share sheet,
 * both of which fire visibilitychange, do not each cost a fetch.
 *
 * Loop guards, in the order they are consulted:
 *   - storage health: if sessionStorage or localStorage throws, EVERY guard
 *     below is broken at once, so we never auto-reload at all and degrade to
 *     the pill (the review proved the fail-open version loops unbounded);
 *   - a sessionStorage timestamp caps checks to one per minute, so a
 *     misbehaving visibilitychange stream cannot hammer the endpoint;
 *   - reloads share one cooldown with the stale-chunk handler in main.tsx
 *     and the deliberate sign-out/invite reloads (see reloadGuard.ts);
 *   - a localStorage marker remembers which deployed version we already
 *     auto-reloaded toward; if we come back (even after a full PWA
 *     relaunch) still running the old bundle, that version never gets a
 *     second automatic attempt - it gets the pill, immediately, without
 *     waiting for the next throttled check;
 *   - 'stale' is not sticky, so a tab that briefly looked out of date during
 *     a rollout stops claiming to be once the edges agree;
 *   - 'dev' builds and non-JSON/failed responses are ignored entirely, so a
 *     local dev server or a captive portal can never show the pill.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { BUILD_VERSION } from './buildInfo';
import {
  clearRetryTarget,
  markReload,
  msSinceLastReload,
  readRetryTarget,
  reloadGuardsHealthy,
  staleChunkReloadHeld,
  subscribeStaleChunkHold,
  writeRetryTarget,
} from './reloadGuard';
import { decideAutoUpdate, hasRegisteredUnsavedWork, uploadInFlight } from './unsavedWork';
import { domHasUnsavedText, modalIsOpen } from './unsavedWorkDom';

/**
 * Just long enough to absorb the visibilitychange churn from the iOS
 * keyboard, the share sheet and app-switcher previews. Anything longer
 * recreates the bug this round exists to fix.
 */
const HIDDEN_MS_BEFORE_CHECK = 2 * 1000;
const CHECK_GUARD_KEY = 'ldg-freshness-checked-at';
const CHECK_GUARD_MS = 60 * 1000;

function guardAllowsCheck(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(CHECK_GUARD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < CHECK_GUARD_MS) return false;
    window.sessionStorage.setItem(CHECK_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    // Storage unavailable: still allow the CHECK - it is a harmless fetch of
    // a no-store endpoint a couple hundred bytes long, bounded by foreground
    // gestures. The RELOAD stays forbidden (guardsHealthy fails closed), so
    // an unthrottled check stream cannot become an unthrottled reload stream.
    return true;
  }
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (
      typeof data === 'object' &&
      data !== null &&
      typeof (data as { version?: unknown }).version === 'string'
    ) {
      return (data as { version: string }).version;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reports whether this tab is running an outdated build, and whether we have
 * decided to ask rather than act.
 *
 * `stale` - /version.json disagrees with this bundle. Clears again if a later
 *           check agrees, which is how a mid-deploy CDN skew resolves itself.
 * `prompt` - stale AND an automatic update was judged unsafe, so
 *            <FreshnessPill /> should offer a manual Reload.
 *
 * When it is safe we never reach `prompt`: the page reloads instead.
 *
 * Both are also true once a stale-chunk reload was held (reloadGuard.ts,
 * train follow-up 2026-10-24): a part of the Hub that loads on demand could
 * not load its file after an update, and reloading would have thrown work
 * away. That stays true for the page's life, because the part that failed
 * cannot load again until a reload, so the pill's Reload stays on screen.
 *
 * @param pathname current route, so a client-side navigation is treated as an
 *   opportunity to apply a pending update (the reload lands on the route the
 *   person just asked for, which is about as invisible as this gets).
 */
export function useVersionFreshness(pathname?: string): { stale: boolean; prompt: boolean } {
  const [stale, setStale] = useState(false);
  const [prompt, setPrompt] = useState(false);

  // The deployed version we last observed, so a navigation can re-run the
  // safety decision without paying for another fetch.
  const deployedRef = useRef<string | null>(null);

  /**
   * Re-runs the safety decision against the version we last fetched. Cheap
   * and side-effect free apart from the reload itself, so both the fetch path
   * and a route change can call it without paying for another request.
   */
  const applyDecision = useCallback(() => {
    const deployed = deployedRef.current;
    if (!deployed || deployed === BUILD_VERSION) return;

    const decision = decideAutoUpdate({
      stale: true,
      hasUnsavedWork: hasRegisteredUnsavedWork() || domHasUnsavedText(),
      uploadInFlight: uploadInFlight(),
      modalOpen: modalIsOpen(),
      msSinceLastReload: msSinceLastReload(),
      alreadyRetriedThisVersion: readRetryTarget() === deployed,
      guardsHealthy: reloadGuardsHealthy(),
    });

    if (decision.action === 'reload') {
      // The marker must be DURABLY written before the navigation starts, or
      // a reload that fails to take comes back with no memory that it was
      // ever tried, and tries again, and again. writeRetryTarget reads its
      // own write back; a false return means the guard is not really there,
      // so fall back to the pill instead of reloading on trust.
      if (!writeRetryTarget(deployed)) {
        setPrompt(true);
        return;
      }
      markReload();
      window.location.reload();
      return;
    }
    setPrompt(decision.action === 'prompt');
  }, []);

  useEffect(() => {
    // A dev build has no meaningful version to compare.
    if (BUILD_VERSION === 'dev') return;

    const marker = readRetryTarget();
    if (marker === BUILD_VERSION) {
      // We auto-reloaded toward this exact build and here it is: the reload
      // worked. Drop the marker so a LATER deploy gets its own attempt.
      clearRetryTarget();
    } else if (marker !== null) {
      // We auto-reloaded toward `marker` at some point - possibly launches
      // ago; the marker is localStorage precisely so it survives a PWA
      // relaunch - and we are STILL not running it. This install is not
      // picking up reloads (stuck shell, stubborn edge), so never spend
      // another automatic attempt on that version: show the pill NOW, from
      // the marker alone, instead of flashing one more useless reload and
      // making the person wait out the 60s check throttle to learn what we
      // already know.
      deployedRef.current = marker;
      setStale(true);
      setPrompt(true);
    }

    let hiddenAt: number | null = null;
    let cancelled = false;

    const check = async () => {
      if (!guardAllowsCheck()) return;
      const deployed = await fetchDeployedVersion();
      if (cancelled || !deployed) return;
      deployedRef.current = deployed;
      if (deployed !== BUILD_VERSION) {
        setStale(true);
        applyDecision();
        return;
      }
      // Deliberately NOT sticky. A deploy rolls out across CDN edges one at a
      // time, so for a minute or so index.html can come from an edge that
      // already has the new build while /version.json still answers with the
      // old one - this tab reads as "stale" while actually being newer.
      // Checks now run on mount, foreground and navigation, so that window
      // gets hit; clear the flag once the skew settles rather than leaving a
      // pill up for the rest of the session.
      setStale(false);
      setPrompt(false);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      // Coming back to the foreground is the moment we most want to act: the
      // app was idle while hidden, and on an installed PWA a reload here
      // reads as part of the launch rather than as the page yanking itself
      // out from under somebody.
      if (hiddenAt === null || Date.now() - hiddenAt >= HIDDEN_MS_BEFORE_CHECK) {
        void check();
      }
      hiddenAt = null;
    };

    const onPageShow = (event: PageTransitionEvent) => {
      // Restored from bfcache: the JS heap is from whenever the page was
      // frozen, which can be arbitrarily old - always worth a check.
      if (event.persisted) void check();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    // One check on mount. A tab opened from a home-screen icon may already be
    // running a bundle that was replaced while the app was closed, and it
    // never fires visibilitychange because it was never hidden.
    void check();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [applyDecision]);

  // A route change is the other invisible moment to apply a pending update:
  // the person asked for a new screen, so a full load delivers exactly what
  // they asked for, just from the new build. No fetch here - only the
  // already-known version is re-judged, because whatever was blocking the
  // update (an open composer, a modal) has usually just been left behind.
  useEffect(() => {
    if (BUILD_VERSION === 'dev') return;
    if (!stale || pathname === undefined) return;
    applyDecision();
  }, [pathname, stale, applyDecision]);

  // A held stale-chunk reload shows the pill the same way an unsafe version
  // update does. Read every render rather than copied into state, so the pill
  // is already up when it mounts after the hold.
  const chunkHeld = useSyncExternalStore(
    subscribeStaleChunkHold,
    staleChunkReloadHeld,
    () => false
  );

  return { stale: stale || chunkHeld, prompt: prompt || chunkHeld };
}

// The visible pill itself (a tiny JSX component) lives in GlobalChrome.tsx
// as <FreshnessPill />, since this lib file is plain .ts. It renders null
// unless this hook reports `prompt`, which now means "stale AND we judged an
// automatic update unsafe" rather than simply "stale".
